/**
 * fetchWithRetry — wraps native fetch with automatic retries for transient errors:
 * - HTTP 429 (Too Many Requests, respecting Retry-After header)
 * - HTTP 502, 503, 504 (Bad Gateway, Service Unavailable, Gateway Timeout)
 * - Network failures (TypeError: Failed to fetch, DNS drop, connection reset)
 * - Request timeouts (via per-attempt AbortController)
 *
 * Essential for the PWA and classroom tablets where Wi-Fi can drop momentarily
 * or cloud containers experience cold starts / scale-up latency.
 */
import { sleep } from './sleep';

export interface FetchWithRetryOptions extends RequestInit {
    /** Maximum retry attempts after the initial call (default: 3, total 4 attempts) */
    maxRetries?: number;
    /** Per-attempt timeout in milliseconds. Default 0 (disabled). Set to > 0 to enable per-attempt timeout. */
    timeoutMs?: number;
    /** Whether to retry on network failures / timeouts (default: true) */
    retryOnNetworkError?: boolean;
    /** HTTP status codes that trigger a retry (default: [429, 502, 503, 504]) */
    retryStatusCodes?: number[];
}

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_STATUSES = [429, 502, 503, 504];
const DEFAULT_BACKOFF_MS = [600, 1200, 2400];

export async function fetchWithRetry(
    input: string,
    options?: FetchWithRetryOptions,
): Promise<Response> {
    const {
        maxRetries = DEFAULT_MAX_RETRIES,
        timeoutMs = 0,
        retryOnNetworkError = true,
        retryStatusCodes = DEFAULT_RETRY_STATUSES,
        signal: callerSignal,
        ...fetchInit
    } = options || {};

    let lastResponse: Response | null = null;
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        // If caller explicitly aborted before this attempt, don't execute
        if (callerSignal?.aborted) {
            throw callerSignal.reason || new Error('Request was aborted by caller');
        }

        const controller = timeoutMs > 0 ? new AbortController() : null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let timedOut = false;

        if (controller && timeoutMs > 0) {
            timeoutId = setTimeout(() => {
                timedOut = true;
                controller.abort(new Error(`Request timed out after ${timeoutMs}ms`));
            }, timeoutMs);
        }

        let cleanupCallerAbort: (() => void) | null = null;
        if (callerSignal && controller) {
            const onAbort = () => {
                if (timeoutId) clearTimeout(timeoutId);
                controller.abort(callerSignal.reason);
            };
            callerSignal.addEventListener('abort', onAbort, { once: true });
            cleanupCallerAbort = () => callerSignal.removeEventListener('abort', onAbort);
        }

        const effectiveSignal = controller ? controller.signal : callerSignal;
        const requestOptions = effectiveSignal
            ? { ...fetchInit, signal: effectiveSignal as any }
            : fetchInit;

        try {
            const response = await fetch(input, requestOptions);

            if (timeoutId) clearTimeout(timeoutId);
            if (cleanupCallerAbort) cleanupCallerAbort();

            lastResponse = response;
            lastError = null;

            // Check if status code warrants a retry
            const isRetryableStatus = retryStatusCodes.includes(response.status);
            if (!isRetryableStatus) {
                return response;
            }

            // On last attempt, return response so caller can inspect status
            if (attempt === maxRetries) {
                console.warn(`[fetchWithRetry] ${input} — status ${response.status} after ${attempt + 1} attempts, giving up`);
                return response;
            }

            // Calculate backoff time
            const retryAfterHeader = response.headers?.get('retry-after');
            const waitMs = retryAfterHeader
                ? Math.max(100, parseInt(retryAfterHeader, 10) * 1000)
                : (DEFAULT_BACKOFF_MS[attempt] ?? 2400);

            console.warn(`[fetchWithRetry] ${input} — status ${response.status}, retrying in ${waitMs}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
            await sleep(waitMs);
        } catch (err: unknown) {
            if (timeoutId) clearTimeout(timeoutId);
            if (cleanupCallerAbort) cleanupCallerAbort();

            lastError = err;

            // If caller aborted explicitly, never retry
            if (callerSignal?.aborted) {
                throw err;
            }

            // If network error retries disabled, re-throw immediately
            if (!retryOnNetworkError) {
                throw err;
            }

            // On last attempt, re-throw error
            if (attempt === maxRetries) {
                console.warn(`[fetchWithRetry] ${input} — error "${(err as Error)?.message || err}" after ${attempt + 1} attempts, giving up`);
                throw err;
            }

            const waitMs = DEFAULT_BACKOFF_MS[attempt] ?? 2400;
            console.warn(`[fetchWithRetry] ${input} — ${timedOut ? 'timed out' : 'network error'}, retrying in ${waitMs}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
            await sleep(waitMs);
        }
    }

    if (lastResponse) return lastResponse;
    throw lastError || new Error(`[fetchWithRetry] Unknown error fetching ${input}`);
}
